import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { getAllSongs, saveSong, getBlob, deleteSong as dbDelete, getSetting, setSetting } from "@/lib/db";
import { parseMetadata } from "@/lib/metadata";
import { translations } from "@/i18n";

const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

const ACCEPTED = [".mp3", ".wav", ".m4a", ".flac"];
const uid = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 8);

export function AppProvider({ children }) {
  const audioRef = useRef(null);
  if (!audioRef.current && typeof Audio !== "undefined") audioRef.current = new Audio();

  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentId, setCurrentId] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [shuffle, setShuffle] = useState(false);
  const [repeat, setRepeat] = useState("off"); // off | all | one
  const [fullOpen, setFullOpen] = useState(false);
  const [uploading, setUploading] = useState(false);

  // settings
  const [language, setLanguageState] = useState("pt");
  const [bg, setBgState] = useState({ type: "gradient", color: "#050505", gradient: "linear-gradient(160deg, #2b0b3a 0%, #050505 60%)", image: null });

  // install
  const deferredPrompt = useRef(null);
  const [canInstall, setCanInstall] = useState(false);
  const [installed, setInstalled] = useState(false);

  const songsRef = useRef([]);
  const currentIdRef = useRef(null);
  const shuffleRef = useRef(false);
  const repeatRef = useRef("off");
  const objectUrl = useRef(null);
  const playNextRef = useRef(() => {});

  useEffect(() => { songsRef.current = songs; }, [songs]);
  useEffect(() => { currentIdRef.current = currentId; }, [currentId]);
  useEffect(() => { shuffleRef.current = shuffle; }, [shuffle]);
  useEffect(() => { repeatRef.current = repeat; }, [repeat]);

  const t = useCallback(
    (key, arg) => {
      const dict = translations[language] || translations.pt;
      const val = dict[key];
      return typeof val === "function" ? val(arg) : val === undefined ? key : val;
    },
    [language]
  );

  // ---- init ----
  useEffect(() => {
    (async () => {
      const list = await getAllSongs();
      setSongs(list);
      const lang = await getSetting("language", navigator.language?.startsWith("en") ? "en" : "pt");
      setLanguageState(lang);
      document.documentElement.lang = lang;
      const savedBg = await getSetting("bg", null);
      if (savedBg) setBgState(savedBg);
      const vol = await getSetting("volume", 1);
      setVolume(vol);
      if (audioRef.current) audioRef.current.volume = vol;
      setLoading(false);
    })();
  }, []);

  // ---- install prompt ----
  useEffect(() => {
    if (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone) setInstalled(true);
    const onPrompt = (e) => {
      e.preventDefault();
      deferredPrompt.current = e;
      setCanInstall(true);
    };
    const onInstalled = () => {
      setInstalled(true);
      setCanInstall(false);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt.current) return;
    deferredPrompt.current.prompt();
    await deferredPrompt.current.userChoice;
    deferredPrompt.current = null;
    setCanInstall(false);
  }, []);

  // ---- audio events ----
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTime = () => setProgress(audio.currentTime);
    const onMeta = () => setDuration(audio.duration);
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnded = () => {
      if (repeatRef.current === "one") {
        audio.currentTime = 0;
        audio.play();
        return;
      }
      playNextRef.current();
    };
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onMeta);
    audio.addEventListener("durationchange", onMeta);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("ended", onEnded);
    return () => {
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onMeta);
      audio.removeEventListener("durationchange", onMeta);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("ended", onEnded);
    };
  }, []);

  const setMediaSession = useCallback((song) => {
    if (!("mediaSession" in navigator) || !song) return;
    navigator.mediaSession.metadata = new window.MediaMetadata({
      title: song.title,
      artist: song.artist,
      album: song.album || "Z Music",
      artwork: song.cover ? [{ src: song.cover, sizes: "512x512", type: "image/jpeg" }] : [],
    });
  }, []);

  const loadAndPlay = useCallback(
    async (id) => {
      const song = songsRef.current.find((s) => s.id === id);
      const audio = audioRef.current;
      if (!song || !audio) return;
      const blob = await getBlob(id);
      if (!blob) {
        toast.error("Arquivo indisponível");
        return;
      }
      if (objectUrl.current) URL.revokeObjectURL(objectUrl.current);
      objectUrl.current = URL.createObjectURL(blob);
      audio.src = objectUrl.current;
      audio.volume = volume;
      setCurrentId(id);
      setProgress(0);
      setMediaSession(song);
      try {
        await audio.play();
      } catch (e) {
        /* autoplay blocked until interaction */
      }
    },
    [volume, setMediaSession]
  );

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (!currentIdRef.current) {
      if (songsRef.current[0]) loadAndPlay(songsRef.current[0].id);
      return;
    }
    if (audio.paused) audio.play();
    else audio.pause();
  }, [loadAndPlay]);

  const playNext = useCallback(() => {
    const list = songsRef.current;
    if (!list.length) return;
    const idx = list.findIndex((s) => s.id === currentIdRef.current);
    let next;
    if (shuffleRef.current && list.length > 1) {
      do {
        next = Math.floor(Math.random() * list.length);
      } while (next === idx);
    } else {
      next = idx + 1;
      if (next >= list.length) {
        if (repeatRef.current === "all") next = 0;
        else {
          if (audioRef.current) audioRef.current.pause();
          return;
        }
      }
    }
    loadAndPlay(list[next].id);
  }, [loadAndPlay]);

  const playPrev = useCallback(() => {
    const audio = audioRef.current;
    const list = songsRef.current;
    if (!list.length || !audio) return;
    if (audio.currentTime > 3) {
      audio.currentTime = 0;
      return;
    }
    const idx = list.findIndex((s) => s.id === currentIdRef.current);
    let prev = idx - 1;
    if (prev < 0) prev = repeatRef.current === "all" ? list.length - 1 : 0;
    loadAndPlay(list[prev].id);
  }, [loadAndPlay]);

  useEffect(() => {
    playNextRef.current = playNext;
  }, [playNext]);

  useEffect(() => {
    if (!("mediaSession" in navigator)) return;
    try {
      navigator.mediaSession.setActionHandler("play", () => audioRef.current?.play());
      navigator.mediaSession.setActionHandler("pause", () => audioRef.current?.pause());
      navigator.mediaSession.setActionHandler("nexttrack", () => playNext());
      navigator.mediaSession.setActionHandler("previoustrack", () => playPrev());
    } catch (e) {}
  }, [playNext, playPrev]);

  const seek = useCallback((val) => {
    if (audioRef.current) {
      audioRef.current.currentTime = val;
      setProgress(val);
    }
  }, []);

  const changeVolume = useCallback((val) => {
    setVolume(val);
    if (audioRef.current) audioRef.current.volume = val;
    setSetting("volume", val);
  }, []);

  // ---- library ----
  const addFiles = useCallback(
    async (fileList) => {
      const files = Array.from(fileList).filter((f) =>
        ACCEPTED.some((ext) => f.name.toLowerCase().endsWith(ext)) || f.type.startsWith("audio/")
      );
      if (!files.length) {
        toast.error(t("invalidFile"));
        return;
      }
      setUploading(true);
      let count = 0;
      for (const file of files) {
        const meta = await parseMetadata(file);
        const nameNoExt = file.name.replace(/\.[^/.]+$/, "");
        const song = {
          id: uid(),
          title: meta.title || nameNoExt || t("unknownTitle"),
          artist: meta.artist || t("unknownArtist"),
          album: meta.album || null,
          cover: meta.cover || null,
          fileName: file.name,
          mime: file.type || "audio/mpeg",
          size: file.size,
          createdAt: Date.now() + count,
        };
        await saveSong(song, file);
        setSongs((prev) => [...prev, song]);
        count++;
      }
      setUploading(false);
      toast.success(t("added", count));
    },
    [t]
  );

  const removeSong = useCallback(
    async (id) => {
      await dbDelete(id);
      setSongs((prev) => prev.filter((s) => s.id !== id));
      if (currentIdRef.current === id) {
        if (audioRef.current) audioRef.current.pause();
        setCurrentId(null);
        setFullOpen(false);
      }
      toast.success(t("removed"));
    },
    [t]
  );

  const downloadSong = useCallback(
    async (song) => {
      const blob = await getBlob(song.id);
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = song.fileName || `${song.title}.mp3`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success(t("downloadStarted"));
    },
    [t]
  );

  // ---- settings setters ----
  const setLanguage = useCallback((lang) => {
    setLanguageState(lang);
    document.documentElement.lang = lang;
    setSetting("language", lang);
  }, []);

  const setBg = useCallback((next) => {
    setBgState(next);
    setSetting("bg", next);
  }, []);

  const currentSong = songs.find((s) => s.id === currentId) || null;

  const value = {
    songs, loading, currentSong, currentId, isPlaying, progress, duration, volume,
    shuffle, repeat, fullOpen, uploading,
    setFullOpen, togglePlay, loadAndPlay, playNext, playPrev, seek, changeVolume,
    setShuffle, setRepeat, addFiles, removeSong, downloadSong,
    language, setLanguage, bg, setBg, t,
    canInstall, installed, promptInstall, ACCEPTED,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}
